import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Shield, Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { SERVICES } from '../data/services';

const primaryNav = [
  { path: '/', label: 'Home' },
  { path: '/about', label: 'About' },
  { path: '/services', label: 'Services', hasMega: true },
  { path: '/insights', label: 'Insights' },
  { path: '/the-cyber-guy', label: 'The Cyber Guy' },
  { path: '/contact', label: 'Contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-950/90 backdrop-blur-xl border-b border-cyan-500/15 shadow-lg shadow-slate-950/40'
          : 'bg-slate-950/60 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-home-link">
            <div className="p-2 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-lg border border-cyan-400/30 group-hover:border-cyan-300/60 transition-colors">
              <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-cyan-300" />
            </div>
            <div className="leading-tight">
              <div className="text-base sm:text-lg font-bold text-white tracking-tight">KENNYVICOPS</div>
              <div className="text-[10px] sm:text-xs text-cyan-300/90 uppercase tracking-widest">
                Cyber Solutions Inc
              </div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {primaryNav.map((item) =>
              item.hasMega ? (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={() => setMegaOpen(true)}
                  onMouseLeave={() => setMegaOpen(false)}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-1 text-sm font-medium transition-colors ${
                        isActive ? 'text-cyan-300' : 'text-slate-300 hover:text-white'
                      }`
                    }
                    data-testid="nav-services"
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${megaOpen ? 'rotate-180' : ''}`} />
                  </NavLink>
                  {megaOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[min(90vw,720px)] z-50">
                      <div className="bg-slate-950/98 backdrop-blur-xl border border-cyan-500/20 rounded-2xl shadow-2xl shadow-slate-950/60 p-6">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                          {SERVICES.map((s) => (
                            <Link
                              key={s.slug}
                              to={`/services/${s.slug}`}
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-slate-800/60 transition-colors group/item"
                              data-testid={`mega-${s.slug}`}
                            >
                              <div className="p-2 rounded-md bg-slate-800/70 border border-cyan-500/20 group-hover/item:border-cyan-400/60 transition-colors">
                                <s.icon className="w-4 h-4 text-cyan-300" />
                              </div>
                              <div>
                                <div className="text-white font-medium text-sm">{s.name}</div>
                                <div className="text-xs text-slate-400 leading-snug mt-0.5 line-clamp-2">
                                  {s.short}
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-cyan-500/15 flex justify-between items-center">
                          <span className="text-xs text-slate-400">Six core services. One practical approach.</span>
                          <Link
                            to="/services"
                            className="text-sm text-cyan-300 hover:text-cyan-200 font-medium"
                          >
                            Explore all services →
                          </Link>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `relative text-sm font-medium transition-colors ${
                      isActive ? 'text-cyan-300' : 'text-slate-300 hover:text-white'
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link to="/security-health-check">
              <Button
                data-testid="header-cta-healthcheck"
                variant="outline"
                className="px-4 py-2.5 bg-transparent border-cyan-400/50 text-cyan-200 hover:bg-cyan-400/10 hover:text-white text-sm font-semibold rounded-full transition-all"
              >
                Free Health Check
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="header-cta-consultation"
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-sm font-semibold rounded-full shadow-lg shadow-cyan-500/20 transition-all"
              >
                Request a Consultation
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden p-2 text-cyan-300 hover:bg-cyan-500/10 rounded-lg transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-slate-950/98 backdrop-blur-xl border-t border-cyan-500/15 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="px-4 py-5 space-y-1">
            {primaryNav.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `block py-3 px-3 rounded-lg text-base font-medium transition-colors ${
                    isActive ? 'bg-cyan-500/10 text-cyan-300' : 'text-slate-200 hover:bg-slate-800/70'
                  }`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="pt-3 mt-3 border-t border-cyan-500/15">
              <p className="px-3 pb-2 text-xs uppercase tracking-widest text-cyan-300/80">Services</p>
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="block py-2.5 px-3 text-sm text-slate-300 hover:text-white hover:bg-slate-800/70 rounded-lg"
                  data-testid={`mobile-mega-${s.slug}`}
                >
                  {s.name}
                </Link>
              ))}
            </div>
            <div className="pt-4 space-y-2">
              <Link to="/security-health-check">
                <Button
                  data-testid="mobile-cta-healthcheck"
                  variant="outline"
                  className="w-full py-5 bg-transparent border-cyan-400/60 text-cyan-200 hover:bg-cyan-400/10 rounded-full"
                >
                  Free Health Check
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="w-full py-5 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full">
                  Request a Consultation
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
